/**
 * Lazy route loading that cannot strand the user.
 *
 * Route splitting was reverted previously because navigation sometimes hung —
 * "sometimes too long, so the user keeps clicking". Three separate causes, all
 * handled here and in ./prefetch.ts:
 *
 *  1. The chunk was only requested on click, over a cold network.
 *     -> ./prefetch.ts warms every route during idle time after first paint.
 *
 *  2. react-router v7 wraps navigation in startTransition, so React keeps the
 *     PREVIOUS page on screen while the chunk loads and shows no fallback. The
 *     page looks frozen. -> ./navProgress.ts drives a visible top progress bar.
 *
 *  3. A rejected import() is permanent. React.lazy caches the rejected promise,
 *     Suspense never resolves, and the route is dead until a manual refresh.
 *     This is the "forever" case, and it happens routinely after a deploy
 *     replaces hashed chunk files while a tab is still open.
 *     -> retry with backoff, then one guarded reload, then a visible error.
 */

import { lazy, type ComponentType } from "react";

type ModuleLoader = () => Promise<Record<string, unknown>>;

export interface LazyRoute {
  Component: ComponentType;
  /** Starts (or joins) the chunk fetch without rendering. Never rejects. */
  preload: () => Promise<void>;
  /** True once the chunk is in memory, so callers can skip the progress bar. */
  isLoaded: () => boolean;
  name: string;
}

/**
 * Same shape as lazyRoute(), for a page that is imported eagerly instead of
 * code-split. isLoaded() is always true, so prefetch.ts's warm-up and
 * intent-warming both no-op on it — there is nothing left to fetch.
 */
export function eagerRoute(name: string, Component: ComponentType): LazyRoute {
  return {
    name,
    Component,
    isLoaded: () => true,
    preload: () => Promise.resolve(),
  };
}

const RELOAD_FLAG = "a2f:chunk-reloaded";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * A failed dynamic import looks different in every engine, so match broadly.
 * Getting this wrong in the false-negative direction means a stale deploy shows
 * a generic error instead of recovering, which is the outcome we're avoiding.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = String(
    (error as Error)?.message ?? (error as string) ?? "",
  ).toLowerCase();

  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("importing a module script failed") ||
    message.includes("dynamically imported module") ||
    message.includes("chunkloaderror") ||
    message.includes("loading chunk")
  );
}

/**
 * One guarded hard reload. A stale index referencing deleted chunk files cannot
 * be recovered any other way — but a reload loop is far worse than an error
 * message, so it happens at most once per tab.
 */
function attemptRecoveryReload(name: string): boolean {
  try {
    if (window.sessionStorage.getItem(RELOAD_FLAG)) return false;
    window.sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    return false; // storage blocked — better to show the error than risk a loop
  }

  console.warn(
    `[App2Food] chunk "${name}" is missing, most likely a stale build. Reloading once.`,
  );
  window.location.reload();
  return true;
}

/** Clears the guard after a successful load, so a later deploy can recover too. */
function clearRecoveryFlag(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}

export function lazyRoute(
  name: string,
  loader: ModuleLoader,
  /** For pages that use a named export instead of a default one. */
  pick?: (module: Record<string, unknown>) => ComponentType,
): LazyRoute {
  let inflight: Promise<{ default: ComponentType }> | null = null;
  let loaded = false;

  const load = (): Promise<{ default: ComponentType }> => {
    if (inflight) return inflight;

    inflight = (async () => {
      let lastError: unknown;

      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const module = await loader();
          loaded = true;
          clearRecoveryFlag();
          const component = pick
            ? pick(module)
            : (module.default as ComponentType);
          return { default: component };
        } catch (error) {
          lastError = error;
          // 200ms, then 400ms. Long enough to ride out a blip, short enough
          // that a genuinely broken chunk surfaces quickly.
          if (attempt < 2) await sleep(200 * 2 ** attempt);
        }
      }

      // Allow a later attempt to start fresh rather than replaying the
      // rejection forever — this is what React.lazy gets wrong on its own.
      inflight = null;

      if (isChunkLoadError(lastError) && attemptRecoveryReload(name)) {
        // Reload is underway; never settle, so no error UI flashes first.
        return new Promise<{ default: ComponentType }>(() => {});
      }

      throw lastError;
    })();

    return inflight;
  };

  return {
    name,
    Component: lazy(load) as LazyRoute["Component"],
    isLoaded: () => loaded,
    preload: () =>
      load().then(
        () => undefined,
        () => undefined, // a failed warm-up must never surface as an error
      ),
  };
}
